import IBaseListingResponse from "../../utils/api/base-listing-response";
import { ICustomeUser } from "../base/base-model";
import {
  CustomerDissatisfactionOperationalSignalKey,
  CustomerSatisfactionStatus,
} from "./enum";
import {
  IClientOrderSatisfaction,
  ICustomerDissatisfactionReason,
  ICustomerSatisfactionAudit,
  ICustomerSatisfactionAuditReasonSnapshot,
  IOrderCustomerSatisfaction,
} from "./response";

const unwrapApiResource = (response: any) =>
  response?.success !== undefined && response?.data !== undefined
    ? response.data
    : response;

export const normalizeListingResponse = <T>(
  response: any,
  mapper: (item: any) => T,
): IBaseListingResponse<T> => {
  const payload = unwrapApiResource(response);
  const rows = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : [];

  return {
    ...payload,
    data: rows.map(mapper),
    total:
      payload?.total ??
      payload?.totalCount ??
      payload?.meta?.total ??
      rows.length,
  };
};

export const mapUser = (user: any): ICustomeUser | null => {
  if (!user) return null;

  return {
    id: user.id,
    email: user.email,
    name: user.name ?? user.full_name ?? user.fullName,
    fullName: user.full_name ?? user.fullName,
    firstName: user.first_name ?? user.firstName,
    lastName: user.last_name ?? user.lastName,
    mobile: user.mobile,
    cleanMobile: user.clean_mobile ?? user.cleanMobile,
  };
};

export const normalizeOperationalSignals = (
  signals: any,
): CustomerDissatisfactionOperationalSignalKey[] => {
  if (!Array.isArray(signals)) return [];

  return signals
    .map((signal) => {
      if (typeof signal === "string") return signal;
      return (
        signal?.key ??
        signal?.operational_signal ??
        signal?.operationalSignal ??
        signal?.value
      );
    })
    .filter(Boolean);
};

export const mapCustomerDissatisfactionReason = (
  item: any,
): ICustomerDissatisfactionReason => ({
  id: item.id,
  reason: item.reason,
  operationalSignals: normalizeOperationalSignals(
    item.operational_signals ?? item.operationalSignals,
  ),
  createdAt: item.created_at ?? item.createdAt,
  createdBy: mapUser(item.created_by ?? item.createdBy) as any,
  updatedAt: item.updated_at ?? item.updatedAt,
  updatedBy: mapUser(item.updated_by ?? item.updatedBy) as any,
  deletedAt: item.deleted_at ?? item.deletedAt,
  deletedBy: mapUser(item.deleted_by ?? item.deletedBy) as any,
});

export const mapOrderCustomerSatisfaction = (
  item: any,
): IOrderCustomerSatisfaction | null => {
  if (!item) return null;

  return {
    id: item.id,
    status: item.status as CustomerSatisfactionStatus,
    reason: item.reason
      ? mapCustomerDissatisfactionReason(item.reason)
      : item.customer_dissatisfaction_reason || item.customerDissatisfactionReason
        ? mapCustomerDissatisfactionReason(
            item.customer_dissatisfaction_reason ??
              item.customerDissatisfactionReason,
          )
        : null,
    note: item.note ?? null,
    determinedAt: item.determined_at ?? item.determinedAt ?? null,
    determinedBy: mapUser(item.determined_by ?? item.determinedBy),
    createdAt: item.created_at ?? item.createdAt,
    createdBy: mapUser(item.created_by ?? item.createdBy) as any,
    updatedAt: item.updated_at ?? item.updatedAt,
    updatedBy: mapUser(item.updated_by ?? item.updatedBy) as any,
    deletedAt: item.deleted_at ?? item.deletedAt,
    deletedBy: mapUser(item.deleted_by ?? item.deletedBy) as any,
  };
};

const mapReasonSnapshot = (
  item: any,
): ICustomerSatisfactionAuditReasonSnapshot | null => {
  if (!item) return null;

  return {
    id: item.id,
    reason: item.reason ?? null,
    operationalSignals: normalizeOperationalSignals(
      item.operational_signals ?? item.operationalSignals,
    ),
  };
};

export const mapCustomerSatisfactionAudit = (
  item: any,
): ICustomerSatisfactionAudit => ({
  id: item.id,
  action: item.action,
  order: item.order
    ? {
        id: item.order.id,
        number: item.order.number ?? null,
      }
    : null,
  changes: Array.isArray(item.changes)
    ? item.changes.map((change: any) => ({
        field: change.field,
        oldValue: change.old_value ?? change.oldValue,
        oldDisplayValue: change.old_display_value ?? change.oldDisplayValue,
        oldReason: mapReasonSnapshot(change.old_reason ?? change.oldReason),
        newValue: change.new_value ?? change.newValue,
        newDisplayValue: change.new_display_value ?? change.newDisplayValue,
        newReason: mapReasonSnapshot(change.new_reason ?? change.newReason),
      }))
    : [],
  oldValues: item.old_values ?? item.oldValues ?? null,
  newValues: item.new_values ?? item.newValues ?? null,
  changedBy: mapUser(item.changed_by ?? item.changedBy),
  createdAt: item.created_at ?? item.createdAt ?? null,
});

export const mapClientOrderSatisfaction = (
  item: any,
): IClientOrderSatisfaction => ({
  id: item.id,
  number: item.number,
  status: item.status,
  startedAt: item.started_at ?? item.startedAt ?? null,
  finishedAt: item.finished_at ?? item.finishedAt ?? null,
  durationMinutes: item.duration_minutes ?? item.durationMinutes ?? null,
  customerSatisfaction: mapOrderCustomerSatisfaction(
    item.customer_satisfaction ?? item.customerSatisfaction,
  ),
});
